import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabaseRpc } from '@/lib/supabaseClient'
import {
  sendContributorInviteEmail,
  sendContributorInviteSms,
  resendContributorInvite,
  cancelContributorInvite,
  type ContributorInviteEmailParams,
  type ContributorInviteSmsParams,
} from '@/lib/notificationWorkerClient'

export interface ContributorInvitation {
  id: string
  drop_id: string
  recipient_email: string | null
  recipient_phone: string | null
  recipient_name: string | null
  method: 'email' | 'sms'
  status: 'pending' | 'accepted' | 'expired' | 'cancelled'
  resend_count: number
  last_resent_at: string | null
  expires_at: string
  invited_at: string
}

export function useContributorInvitationsQuery(dropId: string | undefined) {
  return useQuery({
    queryKey: ['contributor-invitations', dropId],
    queryFn: async () => {
      if (!dropId) return [] as ContributorInvitation[]
      return supabaseRpc<ContributorInvitation[]>('list_contributor_invitations', {
        p_drop_id: dropId,
      })
    },
    enabled: !!dropId,
  })
}

export function useSendContributorInviteEmailMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: ContributorInviteEmailParams) => sendContributorInviteEmail(params),
    onSuccess: (_data, params) => {
      void qc.invalidateQueries({ queryKey: ['contributor-invitations', params.dropId] })
    },
  })
}

export function useSendContributorInviteSmsMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: ContributorInviteSmsParams) => sendContributorInviteSms(params),
    onSuccess: (_data, params) => {
      void qc.invalidateQueries({ queryKey: ['contributor-invitations', params.dropId] })
    },
  })
}

export function useResendContributorInviteMutation(dropId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) => resendContributorInvite(invitationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contributor-invitations', dropId] })
    },
  })
}

export function useCancelContributorInviteMutation(dropId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) => cancelContributorInvite(invitationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contributor-invitations', dropId] })
    },
  })
}
